// app/src/screens/Generating.jsx — the hardest UX moment (handoff §5), pixel-close. Driven by
// watchJob (RTDB when configured, row-polling on the local rig — the ROW is the only truth);
// unknown steps keep the active stage, never crash (progress-map contract). The failed-render
// card says "No credits were charged" because the hold released in full (C1 §4.4). The filming
// scene counter is DECORATIVE activity (the local rig runs one mock pipeline — per-scene progress
// is a later seam); the stage advance is real.

import React, { useEffect, useRef, useState } from 'react';
import { Check, TriangleAlert, RotateCw, Bell } from 'lucide-react';
import { navigate } from '@common/router.js';
import { watchJob } from '@common/progress.js';
import { getDraft, getBrand } from '@common/drafts.js';
import { useI18n } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { useFlow } from '../lib/flow.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
import { StepIndicator, VideoStage } from '../components/ui.jsx';
import { STAGES, stageForStep, stageIndex } from '../lib/progress-map.js';

const SCENES = 5;

export default function Generating({ jobId }) {
  const { t } = useI18n();
  const { api, refreshCredits } = useSession();
  const { draftId } = useFlow();
  const mobile = useIsMobile();
  const [activeStage, setActiveStage] = useState('filming'); // script + look read as done (comp default)
  const [job, setJob] = useState(null);
  const [filming, setFilming] = useState(1);
  const [meta, setMeta] = useState({ aspect: '9:16', brand: null }); // decorative chip — the draft's look
  const stopRef = useRef(null);

  // resolve the aspect + brand name for the stage chrome from the active draft (decorative)
  useEffect(() => {
    if (!draftId) return;
    let alive = true;
    getDraft(api, draftId).then(async (r) => {
      if (!alive || r.kind !== 'ok') return;
      const aspect = (r.body.settings && r.body.settings.aspect) || '9:16';
      let brand = null;
      if (r.body.brand_id) { const b = await getBrand(api, r.body.brand_id); if (b.kind === 'ok') brand = b.body.name; }
      if (alive) setMeta({ aspect, brand });
    });
    return () => { alive = false; };
  }, [draftId, api]);

  useEffect(() => {
    stopRef.current = watchJob({
      api, jobId,
      onStep: (e) => {
        const s = stageForStep(e && e.step);
        if (s) setActiveStage((cur) => (stageIndex(s) > stageIndex(cur) ? s : cur));
      },
      onState: ({ state, job: row }) => {
        if (row) setJob(row);
        if (state === 'succeeded' && row && row.run_id) {
          refreshCredits();
          navigate(`/result/${encodeURIComponent(row.run_id)}`, { replace: true });
        }
        // 'parked' = a footage task is rendering with no worker attached (WEBHOOKS webhook mode);
        // it is NOT terminal and NOT an error — read it exactly like 'running' so the filming
        // animation keeps saying "rendering". Parking only ever happens during footage (= filming).
        if (state === 'running' || state === 'parked') setActiveStage((cur) => (stageIndex(cur) < 2 ? 'filming' : cur));
      },
    });
    return () => stopRef.current && stopRef.current();
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // decorative scene ticker while filming (matches the comp's cadence)
  useEffect(() => {
    if (activeStage !== 'filming') return;
    const id = setInterval(() => setFilming((n) => (n >= SCENES ? 1 : n + 1)), 620);
    return () => clearInterval(id);
  }, [activeStage]);

  const failed = job && (job.state === 'failed' || job.state === 'refused');
  const activeIdx = stageIndex(activeStage);
  const brandChip = t('generating.brandChip', { brand: (meta.brand || t('app.short')).toUpperCase(), aspect: meta.aspect });

  if (failed) {
    return (
      <div className="view">
        <StepIndicator current="video" size={mobile ? 'sm' : 'lg'} />
        <div style={{ maxWidth: 560, margin: '60px auto', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: 999, background: 'var(--coral-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
            <TriangleAlert size={30} color="var(--coral)" strokeWidth={2.2} />
          </div>
          <h2 className="h-section" style={{ marginBottom: 10 }}>{t('generating.failedTitle')}</h2>
          <p className="muted" style={{ font: '400 15px/1.5 var(--font-body)', margin: '0 0 8px' }}>
            {t('generating.failedNote1')} <b style={{ color: 'var(--ink)' }}>{t('generating.failedNoteBold')}</b> {t('generating.failedNote2')}
          </p>
          {job.error && <p className="muted" style={{ fontSize: 12 }}>{job.error}</p>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 22, flexWrap: 'wrap' }}>
            <button className="q-btn press" onClick={() => navigate('/create/script')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--coral)', color: '#fff', padding: '13px 24px', borderRadius: 999, font: '700 14px var(--font-body)', boxShadow: '0 7px 18px rgba(255,81,71,.28)' }}><RotateCw size={15} color="#fff" strokeWidth={2.2} />{t('generating.tryAgain')}</button>
            <button className="q-btn press btn-ghost" onClick={() => navigate('/create/script')}>{t('generating.editScript')}</button>
          </div>
        </div>
      </div>
    );
  }

  const dot = mobile ? 20 : 22, rgap = mobile ? 11 : 12, rpad = mobile ? '8px 0' : '9px 0', rlabel = mobile ? 13.5 : 14.5;
  const doneRow = (label) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: rgap, padding: rpad }}>
      <span style={{ width: dot, height: dot, borderRadius: 999, background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><Check size={mobile ? 11 : 12} color="#fff" strokeWidth={3.2} /></span>
      <span style={{ font: `500 ${rlabel}px var(--font-body)`, color: 'var(--step-off)' }}>{label}</span>
    </div>
  );
  const pendingRow = (label) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: rgap, padding: rpad }}>
      <span style={{ width: dot, height: dot, borderRadius: 999, background: 'var(--fill-track)', flex: 'none' }} />
      <span style={{ font: `500 ${rlabel}px var(--font-body)`, color: 'var(--text-pending)' }}>{label}</span>
    </div>
  );
  const activeCard = (label, extra) => (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--coral-line)', borderRadius: mobile ? 14 : 16, padding: mobile ? 12 : '13px 15px', boxShadow: '0 3px 12px rgba(255,81,71,.1)', margin: '2px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: rgap }}>
        <span style={{ width: dot, height: dot, borderRadius: 999, background: 'var(--coral)', flex: 'none', animation: 'pulseDot 1.3s ease-in-out infinite' }} />
        <span style={{ font: `700 ${rlabel}px var(--font-body)` }}>{label}</span>
        {extra}
      </div>
      {label === t('generating.stage.filming') && (
        <div style={{ height: mobile ? 5 : 6, borderRadius: 999, background: 'var(--fill-track)', overflow: 'hidden', margin: mobile ? '9px 0 0 31px' : '11px 0 0 34px' }}>
          <div style={{ height: '100%', background: 'var(--coral)', borderRadius: 999, animation: 'barGrow 2.2s ease-in-out infinite' }} />
        </div>
      )}
    </div>
  );

  // per-stage rendering (script + look always done; comp §5)
  const pipeline = STAGES.map((s, i) => {
    if (i < activeIdx) return <React.Fragment key={s.key}>{doneRow(s.key === 'filming' ? t('generating.filmed', { n: SCENES }) : t(s.tKey))}</React.Fragment>;
    if (i === activeIdx) {
      const extra = s.key === 'filming' ? <span className="mono" style={{ font: `700 ${mobile ? 11 : 12}px var(--font-mono)`, color: 'var(--coral)', marginInlineStart: 'auto' }}>{filming} / {SCENES}</span> : null;
      return <React.Fragment key={s.key}>{activeCard(t(s.tKey), extra)}</React.Fragment>;
    }
    return <React.Fragment key={s.key}>{pendingRow(t(s.tKey))}</React.Fragment>;
  });

  const stage = (
    <VideoStage aspect={meta.aspect} mobile={mobile} poster="var(--poster-mint)">
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(105deg,transparent 20%,rgba(255,255,255,.3) 45%,rgba(255,255,255,.08) 55%,transparent 80%)', width: '60%', animation: 'shimmer 2.1s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', insetInlineStart: mobile ? 12 : 13, top: mobile ? 12 : 13, font: `700 ${mobile ? 8.5 : 9.5}px var(--font-mono)`, color: 'rgba(255,255,255,.9)', letterSpacing: '.08em' }}>{brandChip}</div>
      <div style={{ position: 'absolute', insetInline: 0, bottom: mobile ? 14 : 16, textAlign: 'center', font: `600 ${mobile ? 12 : 13}px var(--font-body)`, color: '#fff' }}>{t('generating.filmingScene', { n: filming, total: SCENES })}</div>
    </VideoStage>
  );

  const narration = (
    <div style={{ flex: 1, maxWidth: mobile ? '100%' : 520 }}>
      {!mobile && <span className="mono" style={{ font: '700 10px var(--font-mono)', color: 'var(--text-faint)', letterSpacing: '.1em' }}>{t('generating.building').toUpperCase()}</span>}
      <h2 style={{ font: `700 ${mobile ? 22 : 30}px/1.03 var(--font-body)`, letterSpacing: '-.03em', margin: mobile ? '0 0 16px' : '6px 0 24px' }}>{mobile ? t('generating.headlineShort') : t('generating.headline')}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{pipeline}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: mobile ? 9 : 10, marginTop: mobile ? 14 : 18, background: 'var(--mint-tint)', borderRadius: mobile ? 13 : 14, padding: mobile ? '12px 14px' : '13px 16px' }}>
        <Bell size={mobile ? 15 : 16} color="var(--mint-ink)" strokeWidth={1.9} />
        <span style={{ font: `500 ${mobile ? 12 : 12.5}px var(--font-body)`, color: 'var(--mint-ink-2)' }}>{mobile ? t('generating.leaveNoteShort') : t('generating.leaveNote')}</span>
      </div>
    </div>
  );

  return (
    <div className="view">
      <StepIndicator current="video" size={mobile ? 'sm' : 'lg'} />
      {mobile ? (
        <div style={{ marginTop: 20 }}>
          <div style={{ marginBottom: 20 }}>{stage}</div>
          {narration}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 36, alignItems: 'center', marginTop: 26, flexWrap: 'wrap' }}>
          {stage}
          {narration}
        </div>
      )}
    </div>
  );
}
